<template>
  <div class="page-container">
    <header-title
      title="FIFO Coverage Report"
      @onRefresh="refresh"
      has-refresh
    ></header-title>
    <div class="page-body" ref="pageBodyRef">
      <a-table
        :columns="columns"
        :row-key="(record) => record.id"
        :data-source="dataSource"
        :loading="loading"
        bordered
        :pagination="false"
        :scroll="{ x: currentTableWidth, y: tableYHeight }"
        size="small"
      >
        <template #headerCell="{ title }">
          <template v-if="title === 'objectId'">
            <a-select
              v-model:value="selectedRegionCode"
              @change="regionCodeChange"
              class="sel-sty"
            >
              <a-select-option
                v-for="item in regionCodes"
                :key="item"
                :value="item"
              >
                {{ item }}
              </a-select-option>
            </a-select>
          </template>
        </template>
        <template #bodyCell="{ column, record }">
          <template v-if="!['objectId', 'qty'].includes(column.dataIndex)">
            <div
              v-if="record[column.dataIndex].isSufficient === true"
              class="normal-job tringle"
            ></div>

            <a-tooltip v-if="record[column.dataIndex].isSufficient === false">
              <template #title>
                {{
                  dayjs(record[column.dataIndex].eStart).format(
                    'YYYY-MM-DD HH:mm'
                  )
                }}
              </template>
              <div class="normal-job error tringle"></div>
            </a-tooltip>
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>
<script setup>
  import HeaderTitle from '@/layout/header-title'
  import { ref, computed, createVNode, onMounted, onUnmounted } from 'vue'
  import { useStore } from 'vuex'
  import request from '@/utils/request'
  import { throttle } from '@/utils'
  import dayjs from 'dayjs'
  const store = useStore()
  const defaultCol = [
    {
      title: 'objectId',
      dataIndex: 'objectId',
      width: '240px',
      fixed: 'left',
      customRender({ text, record }) {
        return createVNode('div', { class: 'material-sty' }, [
          createVNode('span', {}, text),
          createVNode(
            'span',
            { class: 'estart-sty' },
            record.estartInsufficient
              ? dayjs(record.estartInsufficient).format('MM-DD HH:mm')
              : ''
          ),
        ])
      },
    },
    {
      title: 'Qty',
      dataIndex: 'qty',
      width: '50px',
      fixed: 'left',
    },
  ]
  const timer = ref(null)
  const columns = ref(defaultCol)
  const netGroupId = computed(() => store.getters['user/currentNetGroup'])
  const dataSource = ref([])
  const selectedRegionCode = ref('')
  const regionCodes = ref([])
  const loading = ref(false)
  const getRegionCodes = () => {
    loading.value = true
    request({
      method: 'post',
      url: '/api/fifoCoverageReport/getRegionCode',
      data: {
        netGroupId: netGroupId.value,
      },
    })
      .then(async (res) => {
        let info = res.body
        regionCodes.value = info
        //default action show first region code data
        selectedRegionCode.value = info[0]
        await getJobIdsAndMaterialsInfo(selectedRegionCode.value)
      })
      .finally(() => {
        loading.value = false
      })
  }
  const currentTableWidth = computed(() => {
    return columns.value.reduce((total, cur) => {
      return total * 1 + cur.width.match(/^\d+/)[0] * 1
    }, 0)
  })
  const tableYHeight = ref(300)
  const pageBodyRef = ref(null)
  const setTableScrollH = () => {
    if (!pageBodyRef.value) return

    const tableHeader = document.querySelector('.ant-table-header')
    const tHeadHeight = tableHeader ? tableHeader.clientHeight : 44
    tableYHeight.value = pageBodyRef.value.clientHeight - tHeadHeight
  }
  onMounted(() => {
    setTimeout(setTableScrollH, 1000)
    window.addEventListener('resize', throttle(setTableScrollH, 500))
  })
  onUnmounted(() => {
    window.removeEventListener('resize', setTableScrollH)
    clearTimeout(timer.value)
  })
  const getJobIdsAndMaterialsInfo = (regionCodeVal) => {
    return new Promise((resolve) => {
      loading.value = true
      request({
        method: 'post',
        url: '/api/fifoCoverageReport/getShowReport',
        data: {
          regionCode: regionCodeVal,
        },
      })
        .then((res) => {
          var info = res.body

          let cols = info.jobIdList.map((item) => {
            return {
              title: item,
              dataIndex: item,
              width: '80px',
            }
          })
          columns.value = [...defaultCol, ...cols]
          dataSource.value = info.materInfoList.map((item, index) => {
            let oth = {}
            info.jobIdList.forEach((m) => {
              const f = info.displayUnitList.find(
                (n) => n.idx_y === index && n.jobId === m
              )

              oth[m] = {
                eStart: f ? f.eStart : null,
                isSufficient: f ? f.isSufficient : null,
                jobId: m,
              }
            })
            return {
              ...item,
              ...oth,
            }
          })
        })
        .catch(() => {
          dataSource.value = []
          columns.value = [...defaultCol]
        })
        .finally(() => {
          resolve()
          loading.value = false
          clearTimeout(timer.value)
          timer.value = setTimeout(() => {
            getJobIdsAndMaterialsInfo(selectedRegionCode.value)
          }, 13200000)
        })
    })
  }
  getRegionCodes()
  const refresh = () => {
    getRegionCodes()
  }
  const regionCodeChange = (v) => {
    getJobIdsAndMaterialsInfo(v)
  }
</script>
<style lang="less" scoped>
  @import '~@/styles/variables.less';
  .page-container {
    height: calc(100vh - 32px);
    :deep(.sel-sty) {
      width: 100%;
    }
    :deep(.material-sty) {
      font-weight: 600;
      display: inline-block;
      vertical-align: middle;
      .estart-sty {
        padding-top: 3px;
        font-size: 14px;
        color: @danger;
        display: block;
      }
    }
    .page-body {
      height: calc(100% - 46px);
    }
    :deep(.normal-job) {
      color: rgb(159, 173, 193);
      font-size: 45px;
      transform: rotate(-90deg);
      -webkit-transform: rotate(-90deg);
    }

    :deep(.tringle) {
      @side_length: 1rem;
      @altitude: (@side_length * (sqrt(8) / 2));
      margin: 0 auto;
      width: 0;
      height: 0;
      border-left: @side_length solid transparent;
      border-right: @side_length solid transparent;
      border-bottom: @altitude solid @green;
      transform: rotate(0);
      -webkit-transform: rotate(0);
    }

    :deep(.error) {
      border-bottom-color: @danger;
    }
  }
</style>
